/**
 * The AI API link every AI feature posts to, managed centrally from Workflow
 * and Forms Management > AI Connection.
 *
 * WHY: the link used to be baked in at build time (`VITE_AI_PROXY_URL`, else
 * `/api/ai/messages`) and a dozen agents hardcoded `/api/ai/messages` on top
 * of that. Pointing the timesheet (or anything else) at a different proxy
 * meant a rebuild and, on the S/4-hosted build, a BSP redeploy. Now an admin
 * sets the link once and every caller resolves it through here.
 *
 * Storage: table `ai_connection_settings`, one row (id `default`). On the
 * Vercel build that is Supabase (migration 00502); on the S/4-hosted build
 * `supabase.from(...)` is served by the in-boundary config store
 * (`/sap/bc/zm12cfg`), so the same row lives in ZTM12_TS_CFG with no extra
 * ABAP. A blank link means "use the build default", so an empty store changes
 * nothing.
 */

export const AI_CONNECTION_TABLE = 'ai_connection_settings'
export const AI_CONNECTION_ROW_ID = 'default'

/** Where the link comes from when nothing is set centrally. */
export function buildDefaultAiProxyUrl(): string {
  return (import.meta.env.VITE_AI_PROXY_URL as string | undefined)?.trim() || '/api/ai/messages'
}

export interface AiConnectionSettings {
  /** Blank = build default. */
  proxyUrl: string
  updatedBy: string | null
  updatedAt: string | null
}

/**
 * A link is either a same-origin path (`/api/ai/messages`, `/sap/bc/zm12ai`)
 * or an https URL. Plain http is allowed for localhost only, so prompts never
 * leave the machine unencrypted. Returns an error message, or null when valid.
 *
 * A model provider's own API host is refused. The browser holds no provider
 * key and must never talk to a provider directly: the link is always the app's
 * AI endpoint or a proxy that holds the key, so which provider answers stays a
 * server decision on every deployment.
 */
export function validateAiProxyUrl(raw: string): string | null {
  const v = raw.trim()
  if (!v) return null
  if (/\s/.test(v)) return 'The link cannot contain spaces.'
  if (v.startsWith('/')) {
    if (v.startsWith('//')) return 'Use a full https:// URL or a path that starts with a single /.'
    return null
  }
  let url: URL
  try {
    url = new URL(v)
  } catch {
    return 'Enter a path such as /api/ai/messages or a full https:// URL.'
  }
  if (isModelProviderHost(url.hostname)) {
    return 'The link cannot be a model provider address. Use the AI endpoint of this app or a proxy that holds the key.'
  }
  if (url.protocol === 'https:') return null
  if (url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1')) return null
  return 'Only https:// links are allowed (http:// only for localhost).'
}

/**
 * True for the API hosts of the model providers themselves (Anthropic, OpenAI,
 * and Azure OpenAI Service resources in every Azure cloud, including Azure
 * Government under `*.openai.azure.us`).
 */
export function isModelProviderHost(hostname: string): boolean {
  const h = hostname.trim().toLowerCase().replace(/\.$/, '')
  if (h === 'api.anthropic.com' || h.endsWith('.anthropic.com')) return true
  if (h === 'api.openai.com' || h.endsWith('.openai.com')) return true
  // <resource>.openai.azure.com / .azure.us / .azure.cn, and the newer
  // <resource>.cognitiveservices.azure.* and <resource>.services.ai.azure.* hosts.
  return /\.(openai|cognitiveservices|services\.ai)\.azure\.[a-z]+$/.test(h)
}

// ─── Runtime cache ─────────────────────────────────────────────────────────────

let override: string | null = null
let loadPromise: Promise<void> | null = null

/** How long an AI call waits for the central setting before using the default. */
const LOAD_WAIT_MS = 3000

function coerce(row: Record<string, unknown> | null | undefined): AiConnectionSettings {
  const url = typeof row?.proxy_url === 'string' ? row.proxy_url.trim() : ''
  return {
    proxyUrl: url && validateAiProxyUrl(url) === null ? url : '',
    updatedBy: typeof row?.updated_by === 'string' ? row.updated_by : null,
    updatedAt: typeof row?.updated_at === 'string' ? row.updated_at : null,
  }
}

/** Read the stored setting. Blank settings (never throws) when the store is unreachable. */
export async function getAiConnectionSettings(): Promise<AiConnectionSettings> {
  try {
    const { supabase } = await import('@/lib/supabase')
    const { data, error } = await supabase
      .from(AI_CONNECTION_TABLE)
      .select('*')
      .eq('id', AI_CONNECTION_ROW_ID)
      .maybeSingle()
    if (error) {
      console.warn('[AI] central connection setting unavailable', error.message)
      return coerce(null)
    }
    return coerce(data as Record<string, unknown> | null)
  } catch (err) {
    console.warn('[AI] central connection setting unavailable', err)
    return coerce(null)
  }
}

function loadOnce(): Promise<void> {
  if (!loadPromise) {
    loadPromise = getAiConnectionSettings().then((s) => {
      override = s.proxyUrl || null
      if (override) console.log(`[AI] Using centrally managed AI link ${override}`)
    })
  }
  return loadPromise
}

/** The link to use right now, without waiting for the central setting. */
export function aiProxyUrl(): string {
  return override || buildDefaultAiProxyUrl()
}

/**
 * The link to use for a request. Waits (briefly) for the central setting on
 * the first call so the very first AI request after page load already goes
 * to the managed link.
 */
export async function resolveAiProxyUrl(): Promise<string> {
  await Promise.race([loadOnce(), new Promise<void>((r) => setTimeout(r, LOAD_WAIT_MS))])
  return aiProxyUrl()
}

/** Save the link (blank clears it back to the build default) and apply it immediately. */
export async function saveAiConnectionSettings(proxyUrl: string, updatedBy: string | null): Promise<AiConnectionSettings> {
  const url = proxyUrl.trim()
  const problem = validateAiProxyUrl(url)
  if (problem) throw new Error(problem)
  const { supabase } = await import('@/lib/supabase')
  const row = {
    id: AI_CONNECTION_ROW_ID,
    proxy_url: url,
    updated_by: updatedBy,
    updated_at: new Date().toISOString(),
  }
  const { error } = await supabase.from(AI_CONNECTION_TABLE).upsert(row, { onConflict: 'id' })
  if (error) throw new Error(error.message)
  override = url || null
  loadPromise = Promise.resolve()
  return coerce(row)
}

export interface AiConnectionTestResult {
  ok: boolean
  status: number | null
  ms: number
  detail: string
}

/**
 * Send the smallest possible request to a link and report what came back.
 * Uses the same body shape every caller sends, so a pass here means the
 * timesheet's calls will reach the model too.
 */
export async function testAiConnection(url: string, model: string): Promise<AiConnectionTestResult> {
  const target = url.trim() || buildDefaultAiProxyUrl()
  const started = performance.now()
  try {
    const { entraAuthHeaders } = await import('@/lib/entraAuth')
    const res = await fetch(target, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await entraAuthHeaders()) },
      body: JSON.stringify({
        model,
        max_tokens: 16,
        messages: [{ role: 'user', content: 'Reply with the single word OK.' }],
      }),
    })
    const ms = Math.round(performance.now() - started)
    const text = await res.text()
    if (!res.ok) return { ok: false, status: res.status, ms, detail: text.slice(0, 300) || res.statusText }
    let reply = ''
    try {
      // The model named here is the one the SERVER reports answered, which on
      // some deployments differs from the id sent above (a tier hint there).
      const body = JSON.parse(text) as { content?: Array<{ type?: string; text?: string }>; model?: string }
      reply = (body.content ?? []).filter((b) => b.type === 'text').map((b) => b.text ?? '').join('').trim()
      if (body.model) reply = `${reply || '(no text)'} from ${body.model}`
    } catch {
      return { ok: false, status: res.status, ms, detail: 'The link answered, but not with a model response.' }
    }
    return { ok: true, status: res.status, ms, detail: reply || 'Connected.' }
  } catch (err) {
    return {
      ok: false,
      status: null,
      ms: Math.round(performance.now() - started),
      detail: err instanceof Error ? err.message : 'The request did not reach the link.',
    }
  }
}

/** Test seam: forget the cached setting. */
export function resetAiConnectionCacheForTests(): void {
  override = null
  loadPromise = null
}
